import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

const fail = (error, fallback) => {
  console.error(fallback, error);
  toast.error(error.response?.data?.detail || fallback);
};

// Workspace (instance) data for one user: list, switch, create, delete. UI/modal state stays in the component.
export const useInstances = ({ userId, currentInstance, onInstanceChange }) => {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const fetchRef = useRef(() => {});

  const adoptActive = async () => {
    const res = await axios.get(`${API}/instances/${userId}/active`);
    if (res.data) onInstanceChange(res.data);
  };

  const fetchInstances = async () => {
    try {
      const response = await axios.get(`${API}/instances/${userId}`);
      setInstances(response.data);
      if (!currentInstance && response.data.length > 0) await adoptActive();
    } catch (error) {
      console.error("Error fetching instances:", error);
    } finally {
      setLoading(false);
    }
  };
  // Latest fetchInstances without re-running the effect when currentInstance/onInstanceChange change
  fetchRef.current = fetchInstances;

  useEffect(() => {
    fetchRef.current();
  }, [userId]);

  const switchInstance = async (instance) => {
    try {
      await axios.post(`${API}/instances/${userId}/active/${instance.instanceId}`);
      onInstanceChange(instance);
      toast.success(`Switched to ${instance.name}`);
      return true;
    } catch (error) {
      fail(error, "Failed to switch workspace");
      return false;
    }
  };

  const createInstance = async ({ name, icon, color }) => {
    setCreating(true);
    try {
      const response = await axios.post(`${API}/instances`, { userId, name, icon, color });
      return response.data;
    } catch (error) {
      fail(error, "Failed to create workspace");
      return null;
    } finally {
      setCreating(false);
    }
  };

  const deleteInstance = async (instanceId) => {
    try {
      await axios.delete(`${API}/instances/${instanceId}`);
      setInstances((prev) => prev.filter((i) => i.instanceId !== instanceId));
      toast.success("Workspace deleted");
      if (currentInstance?.instanceId === instanceId) await adoptActive();
      return true;
    } catch (error) {
      fail(error, "Failed to delete workspace");
      return false;
    }
  };

  const upsertInstance = (instance) =>
    setInstances((prev) => (prev.some((i) => i.instanceId === instance.instanceId)
      ? prev.map((i) => (i.instanceId === instance.instanceId ? instance : i))
      : [...prev, instance]));

  // After onboarding, re-read the server copy of the pending instance (falls back to a locally completed one).
  const resolveCompleted = async (pending) => {
    const res = await axios.get(`${API}/instances/${userId}`).catch(() => null);
    const list = res?.data?.instances || res?.data || [];
    return list.find?.((i) => i.instanceId === pending.instanceId) || { ...pending, onboardingComplete: true };
  };

  return { instances, loading, creating, switchInstance, createInstance, deleteInstance, upsertInstance, resolveCompleted };
};
